import { Component } from '@angular/core';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { Attachment, AttachmentModalService } from './attachment-modal.service';
import { OverlayLoadingSpinnerComponent } from '../../../overlay-loading-spinner/overlay-loading-spinner.component';
import { ToastService } from '../../../toasters/Toast.service';
import { Logger } from '../../../../../../common/core/utilities/logging/logger';
import { firstValueFrom } from 'rxjs';
import { handleApiErrors, UserContextService, UtilityManagementPermissions } from '../../../../../../common';

@Component({
  selector: 'app-view-attachments-dialog',
  standalone: true,
  imports: [ButtonModule, CommonModule, OverlayLoadingSpinnerComponent],
  templateUrl: './view-attachments-dialog.component.html',
  styleUrl: './view-attachments-dialog.component.scss',
})
export class ViewAttachmentsDialogComponent {
  attachments: Attachment[] = [];
  isLoading: boolean = false;

  hasReadAttachmentsPermission: boolean =
    UserContextService?.hasPermission(UtilityManagementPermissions?.Attachments?.Read) ?? false;

  constructor(
    private _attachmentModalService: AttachmentModalService,
    public config: DynamicDialogConfig,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    const attachmentData = this.config.data?.attachmentFiles;
    Logger.debug('Attachment Data at ViewAttachmentsDialogComponent: ', attachmentData);

    if (attachmentData?.attachments) {
      this.attachments = (attachmentData.type === 'multi' ? attachmentData.attachments : [attachmentData.attachments])
        .map((item: any) => ({
          id: item?.id,
          name: item?.name,
          size: item?.size,
          downloadUrl: item?.downloadUrl, // Default empty to avoid undefined issues
          type: item?.type,
          objectURL: item?.objectURL
        }));
    }
    if (attachmentData?.attachment && attachmentData.type === 'single') {
      this.attachments.push({
        id: attachmentData?.attachment?.id,
        name: attachmentData?.attachment?.name,
        size: attachmentData?.attachment?.size,
        downloadUrl: attachmentData?.attachment?.downloadUrl, // Default empty to avoid undefined issues
        type: attachmentData?.attachment?.type,
        objectURL: attachmentData?.attachment?.objectURL
      })
    }
  }

  async onDownload(attachment: Attachment): Promise<void> {
    if (attachment.downloadUrl) {
      this.triggerDownload(attachment.downloadUrl, attachment.name);
    } else {
      try {
        this.isLoading = true;
        const res = await firstValueFrom(this._attachmentModalService.getDownloadURL(attachment.id));

        if (res?.downloadUrl) {
          attachment.downloadUrl = res.downloadUrl ?? ''; // Ensure it's always a string
          attachment.name = res.name ?? 'unknown_file';
          attachment.size = res.size ?? 0;
          this.triggerDownload(res.downloadUrl, attachment.name);
        } else {
          Logger.error('Download URL not found');
        }
      } catch (error: any) {
        handleApiErrors(error, this.toastService);
      } finally {
        this.isLoading = false;
      }
    }
  }

  private triggerDownload(url: string, fileName: string): void {
    if (!url) {
      Logger.error('Download URL is undefined');
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'download';
    link.target = '_blank';
    link.click();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024,
      dm = 2,
      sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'],
      i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  isDocument(file: Attachment): boolean {
    if (!file.name) return false;
    const documentExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase() ?? '';
    return documentExtensions.includes(fileExtension);
  }

  calculateTotalSize(): number {
    return this.attachments.reduce((acc: any, image) => acc + image.size, 0);
  }
}
